import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogOut, Plus, Edit, Trash2, Send, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

function EmployeeDashboard() {
  const { auth, logout } = useContext(AuthContext);
  const [draftEntries, setDraftEntries] = useState([]);
  const [parts, setParts] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const searchRef = useRef(null);
  
  const [formData, setFormData] = useState({
    part_id: '',
    operation_number: '',
    quantity: '',
    defects: '',
    comments: ''
  });

  useEffect(function loadInitialData() {
    loadParts();
    loadDraftEntries();
    // Request push permission and subscribe
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      requestPushPermission();
    }
  }, []);

  const requestPushPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      // Get VAPID public key
      const keyRes = await axios.get(`${API}/push/vapid-key`);
      const vapidKey = keyRes.data.public_key;
      if (!vapidKey) return;
      // Convert base64 to Uint8Array
      const pad = '='.repeat((4 - (vapidKey.length % 4)) % 4);
      const raw = atob((vapidKey + pad).replace(/-/g, '+').replace(/_/g, '/'));
      const appServerKey = new Uint8Array(raw.split('').map(c => c.charCodeAt(0)));
      // Subscribe
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey });
      await axios.post(`${API}/push/subscribe`, { employee_id: auth.user.id, subscription: sub.toJSON() });
    } catch { /* silent fail */ }
  };

  useEffect(function filterPartsOnSearch() {
    if (searchQuery.trim() === '') {
      setFilteredParts(parts);
    } else {
      var query = searchQuery.toLowerCase();
      var filtered = parts.filter(function(part) {
        return part.name.toLowerCase().includes(query) || 
               part.code.toLowerCase().includes(query);
      });
      setFilteredParts(filtered);
    }
  }, [searchQuery, parts]);

  // Close dropdown when clicking outside
  useEffect(function handleClickOutside() {
    function handleClick(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return function() {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  function loadParts() {
    axios.get(API + '/parts')
      .then(function(response) {
        setParts(response.data);
        setFilteredParts(response.data);
      })
      .catch(function(error) {
        console.error('Error loading parts:', error);
      });
  }

  function loadDraftEntries() {
    axios.get(API + '/production-entries/draft/' + auth.user.id)
      .then(function(response) {
        setDraftEntries(response.data);
      })
      .catch(function(error) {
        console.error('Error loading draft entries:', error);
      });
  }

  function handlePartSelect(part) {
    setSelectedPart(part);
    setSearchQuery(part.name + ' - ' + part.code);
    setShowDropdown(false);
    setFormData({ 
      part_id: part.id, 
      operation_number: '', 
      quantity: formData.quantity,
      defects: formData.defects,
      comments: formData.comments
    });
  }

  function handleSearchFocus() {
    setShowDropdown(true);
  }

  function handleSearchChange(e) {
    setSearchQuery(e.target.value);
    setShowDropdown(true);
    // Clear selection if user types
    if (selectedPart && e.target.value !== selectedPart.name + ' - ' + selectedPart.code) {
      setSelectedPart(null);
      setFormData({
        ...formData,
        part_id: '',
        operation_number: ''
      });
    }
  }

  function openNewEntryDialog() {
    setEditingEntry(null);
    setFormData({
      part_id: '',
      operation_number: '',
      quantity: '',
      defects: '',
      comments: ''
    });
    setSelectedPart(null);
    setSearchQuery('');
    setShowEntryDialog(true);
  }

  function openEditEntryDialog(entry) {
    setEditingEntry(entry);
    var part = parts.find(function(p) { return p.id === entry.part_id; });
    setSelectedPart(part);
    if (part) {
      setSearchQuery(part.name + ' - ' + part.code);
    }
    setFormData({
      part_id: entry.part_id,
      operation_number: String(entry.operation_number),
      quantity: String(entry.quantity),
      defects: String(entry.defects || 0),
      comments: entry.comments || ''
    });
    setShowEntryDialog(true);
  }

  function handleSaveEntry(e) {
    e.preventDefault();
    setLoading(true);

    var payload = {
      part_id: formData.part_id,
      operation_number: parseInt(formData.operation_number),
      quantity: parseInt(formData.quantity),
      defects: parseInt(formData.defects) || 0,
      comments: formData.comments || null
    };

    var request;
    if (editingEntry) {
      request = axios.put(API + '/production-entries/' + editingEntry.id, payload);
    } else {
      request = axios.post(API + '/production-entries?employee_id=' + auth.user.id, payload);
    }

    request
      .then(function() {
        toast.success(editingEntry ? 'Записът е актуализиран' : 'Записът е създаден');
        setShowEntryDialog(false);
        loadDraftEntries();
      })
      .catch(function(error) {
        toast.error(error.response?.data?.detail || 'Грешка при запазване');
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function handleDeleteEntry(entryId) {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този запис?')) {
      return;
    }

    axios.delete(API + '/production-entries/' + entryId)
      .then(function() {
        toast.success('Записът е изтрит');
        loadDraftEntries();
      })
      .catch(function(error) {
        toast.error(error.response?.data?.detail || 'Грешка при изтриване');
      });
  }

  function handleSubmitAll() {
    if (draftEntries.length === 0) {
      toast.error('Няма записи за изпращане');
      return;
    }

    if (!window.confirm('Сигурни ли сте, че искате да изпратите всички записи? След изпращане не можете да ги редактирате.')) {
      return;
    }

    setLoading(true);
    axios.post(API + '/production-entries/submit?employee_id=' + auth.user.id)
      .then(function(response) {
        toast.success('Изпратени ' + response.data.count + ' записа');
        setDraftEntries([]);
      })
      .catch(function(error) {
        toast.error(error.response?.data?.detail || 'Грешка при изпращане');
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function handleLogoutClick() {
    if (draftEntries.length > 0) {
      setShowLogoutWarning(true);
    } else {
      logout();
    }
  }

  function handleForceLogout() {
    setShowLogoutWarning(false);
    logout();
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b-2 border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="heading-font text-base sm:text-2xl md:text-3xl font-bold text-slate-900 uppercase truncate leading-tight">
              {auth.user.full_name}
            </h1>
            <p className="text-slate-500 mono-font text-xs sm:text-sm">{auth.user.employee_id}</p>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogoutClick}
            variant="outline"
            size="sm"
            className="border-2 hover:border-red-600 hover:text-red-600 shrink-0 px-2 sm:px-3"
          >
            <LogOut className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline text-xs">Изход</span>
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-8">
        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Button
            data-testid="new-entry-button"
            onClick={openNewEntryDialog}
            className="bg-red-600 hover:bg-red-700 font-bold uppercase h-11 sm:h-12 w-full sm:w-auto sm:flex-none"
          >
            <Plus className="w-5 h-5 mr-2" />
            Нов запис
          </Button>
          
          {draftEntries.length > 0 && (
            <Button
              data-testid="submit-all-button"
              onClick={handleSubmitAll}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 font-bold uppercase h-11 sm:h-12 w-full sm:w-auto sm:flex-none"
            >
              <Send className="w-5 h-5 mr-2" />
              Край на смяната ({draftEntries.length} записа)
            </Button>
          )}
        </div>

        {/* Summary card - without minutes */}
        {draftEntries.length > 0 && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs text-amber-800 uppercase font-medium">Записи</p>
                <p className="text-xl font-bold text-amber-900">{draftEntries.length}</p>
              </div>
              <div>
                <p className="text-xs text-amber-800 uppercase font-medium">Бройки</p>
                <p className="text-xl font-bold text-amber-900">
                  {draftEntries.reduce(function(sum, e) { return sum + e.quantity; }, 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-amber-800 uppercase font-medium">Брак</p>
                <p className="text-xl font-bold text-red-600">
                  {draftEntries.reduce(function(sum, e) { return sum + (e.defects || 0); }, 0)}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Draft entries list - without minutes */}
        <Card className="p-6">
          <h2 className="heading-font text-xl font-bold uppercase mb-4">Записи за днес</h2>
          
          {draftEntries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p className="text-lg">Няма добавени записи</p>
              <p className="text-sm mt-2">Натиснете "Нов запис" за да започнете</p>
            </div>
          ) : (
            <div className="space-y-3">
              {draftEntries.map(function(entry) {
                return (
                  <div 
                    key={entry.id} 
                    className="flex items-start justify-between p-4 bg-slate-50 rounded-lg border gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 text-sm leading-tight">
                        {entry.part?.name || 'Неизвестен'}
                        {entry.part?.code && <span className="text-slate-500 font-normal"> · {entry.part.code}</span>}
                      </div>
                      <div className="text-xs text-slate-600 mt-1">
                        Оп. № {entry.operation_number}{entry.operation?.operation_name ? ` — ${entry.operation.operation_name}` : ''} &nbsp;|&nbsp;
                        <span className="font-medium">{entry.quantity} бр.</span>
                        {entry.defects > 0 && (
                          <span className="text-red-600"> &nbsp;|&nbsp; Брак: {entry.defects}</span>
                        )}
                      </div>
                      {entry.comments && (
                        <div className="text-xs text-slate-500 mt-1 truncate">
                          {entry.comments}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        data-testid={'edit-entry-' + entry.id}
                        onClick={function() { openEditEntryDialog(entry); }}
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={'delete-entry-' + entry.id}
                        onClick={function() { handleDeleteEntry(entry.id); }}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Entry Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent className="w-full max-w-2xl max-h-[92vh] overflow-y-auto mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl uppercase">
              {editingEntry ? 'Редактиране на запис' : 'Нов производствен запис'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveEntry} className="space-y-6">
            {/* Smart search with dropdown */}
            <div ref={searchRef} className="relative">
              <Label className="text-sm uppercase tracking-wide font-medium mb-2 block">
                Търси и избери детайл *
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="part-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  className="h-12 text-base border-2 pl-10 focus:border-red-600"
                  placeholder="Започнете да пишете име или код..."
                  autoComplete="off"
                />
              </div>
              
              {/* Dropdown list */}
              {showDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredParts.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">
                      Няма намерени детайли
                    </div>
                  ) : (
                    filteredParts.map(function(part) {
                      var isSelected = selectedPart && selectedPart.id === part.id;
                      return (
                        <div
                          key={part.id}
                          onClick={function() { handlePartSelect(part); }}
                          className={
                            'p-3 cursor-pointer hover:bg-red-50 border-b last:border-b-0 ' +
                            (isSelected ? 'bg-red-100' : '')
                          }
                        >
                          <div className="font-medium">{part.name}</div>
                          <div className="text-sm text-slate-500">{part.code}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {selectedPart && selectedPart.operations && selectedPart.operations.length > 0 && (
              <div>
                <Label className="text-sm uppercase tracking-wide font-medium mb-2 block">
                  Номер на операция *
                </Label>
                <Select
                  value={formData.operation_number}
                  onValueChange={function(value) {
                    setFormData({
                      ...formData,
                      operation_number: value
                    });
                  }}
                >
                  <SelectTrigger data-testid="operation-select" className="h-12 text-base border-2">
                    <SelectValue placeholder="Изберете операция" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedPart.operations.map(function(op) {
                      return (
                        <SelectItem key={op.id} value={String(op.operation_number)}>
                          № {op.operation_number} — {op.operation_name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-sm uppercase tracking-wide font-medium mb-2 block">
                Изработени бройки *
              </Label>
              <Input
                data-testid="quantity-input"
                type="number"
                min="1"
                required
                value={formData.quantity}
                onChange={function(e) {
                  setFormData({ ...formData, quantity: e.target.value });
                }}
                className="h-12 text-base border-2 focus:border-red-600"
                autoComplete="off"
              />
            </div>

            <div>
              <Label className="text-sm uppercase tracking-wide font-medium mb-2 block">
                Брак (незадължително)
              </Label>
              <Input
                data-testid="defects-input"
                type="number"
                min="0"
                value={formData.defects}
                onChange={function(e) {
                  setFormData({ ...formData, defects: e.target.value });
                }}
                className="h-12 text-base border-2 focus:border-red-600"
                placeholder="Въведи бройки"
                autoComplete="off"
              />
            </div>

            <div>
              <Label className="text-sm uppercase tracking-wide font-medium mb-2 block">
                Коментари
              </Label>
              <Textarea
                data-testid="comments-input"
                value={formData.comments}
                onChange={function(e) {
                  setFormData({ ...formData, comments: e.target.value });
                }}
                className="border-2 focus:border-red-600"
                rows={3}
                autoComplete="off"
              />
            </div>

            <div className="flex gap-3">
              <Button
                data-testid="save-entry-button"
                type="submit"
                disabled={loading || !formData.part_id || !formData.operation_number || !formData.quantity}
                className="flex-1 bg-red-600 hover:bg-red-700 h-12 font-bold uppercase"
              >
                {loading ? 'Запазване...' : 'Запиши'}
              </Button>
              <Button
                data-testid="cancel-entry-button"
                type="button"
                onClick={function() { setShowEntryDialog(false); }}
                variant="outline"
                className="flex-1 h-12 border-2 font-semibold uppercase"
              >
                Отказ
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Logout Warning Dialog */}
      <Dialog open={showLogoutWarning} onOpenChange={setShowLogoutWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-6 h-6" />
              Внимание!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700 mb-4">
              Имате <strong>{draftEntries.length}</strong> неизпратени записа.
            </p>
            <p className="text-slate-600">
              Моля, натиснете <strong>"Край на смяната"</strong> за да изпратите записите преди да излезете.
              В противен случай записите няма да бъдат отчетени.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={function() { setShowLogoutWarning(false); }}
              className="flex-1 bg-green-600 hover:bg-green-700 font-bold"
            >
              Разбрах, ще изпратя записите
            </Button>
            <Button
              onClick={handleForceLogout}
              variant="outline"
              className="flex-1 border-2 border-red-300 text-red-600 hover:bg-red-50"
            >
              Изход без изпращане
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmployeeDashboard;
